// The caster's queue advance (spec 2026-10-16 §4 "Advancing"), cases C55–C63 in
// docs/superpowers/plans/2026-10-16-cast-a-post-web.md Task 5 (Android ports them verbatim).
// A1: ENDED of the current item sends `next {fromIndex}` at once, once per item.
// A2: a load error waits LOAD_ERROR_SKIP_MS, then sends `next` (one bad item cannot stall the queue).
// A3: a failed `next` (network, 5xx, refusal) is retried once after NEXT_RETRY_MS, then given up.
// A4: moved, stopped and STALE_INDEX end the item's state (the caller adopts a stale queue).
// A5: a different index is a new item: all state starts over; old timers do nothing.
// A7 (C64): every next carries a seq; result() ignores an answer whose seq is not the call out.
// A6 (C63): a caster step (⏮/⏭/restart) re-arms the machine for the item now playing, even at
// the same index (Previous at 0 restarts item 0), so its ENDED advances again.

export const LOAD_ERROR_SKIP_MS = 3000;
export const NEXT_RETRY_MS = 1000;
const MAX_FAILURES = 2;

export type AdvanceCommand = { type: "next"; fromIndex: number; seq: number } | { type: "wakeAt"; mono: number };
export type NextOutcome = "moved" | "stopped" | "stale" | "failed";

export class CastAdvancer {
  private index: number | null = null;
  private out = false;
  private done = false;
  private failures = 0;
  private wakeAt: number | null = null;
  private seq = 0; // monotonic across rearm: a seq is never reused
  private outSeq: number | null = null; // the call currently out

  ended(index: number, _mono: number): AdvanceCommand[] {
    this.at(index);
    if (this.out || this.done) return [];
    this.wakeAt = null; // A1: no need to wait for a pending skip
    return this.send(index);
  }

  loadFailed(index: number, mono: number): AdvanceCommand[] {
    this.at(index);
    if (this.out || this.done || this.wakeAt != null) return [];
    this.wakeAt = mono + LOAD_ERROR_SKIP_MS; // A2
    return [{ type: "wakeAt", mono: this.wakeAt }];
  }

  wake(index: number, mono: number): AdvanceCommand[] {
    if (this.index !== index || this.wakeAt == null) return []; // A5
    if (mono < this.wakeAt) return [{ type: "wakeAt", mono: this.wakeAt }];
    this.wakeAt = null;
    if (this.out || this.done) return [];
    return this.send(index);
  }

  result(fromIndex: number, seq: number, outcome: NextOutcome, mono: number): AdvanceCommand[] {
    // A7 (C64): only the answer to the call currently out counts; rearm() invalidates it.
    if (this.index !== fromIndex || this.outSeq === null || this.outSeq !== seq) return [];
    this.out = false;
    this.outSeq = null;
    if (outcome !== "failed") {
      this.done = true; // A4
      return [];
    }
    this.failures += 1; // A3
    if (this.failures >= MAX_FAILURES) {
      this.done = true;
      return [];
    }
    this.wakeAt = mono + NEXT_RETRY_MS;
    return [{ type: "wakeAt", mono: this.wakeAt }];
  }

  /** C63 (A6): a caster step put a (possibly the same) item on air: forget everything, even for the same index. */
  rearm(): void {
    this.outSeq = null;
    this.index = null;
    this.out = false;
    this.done = false;
    this.failures = 0;
    this.wakeAt = null;
  }

  private at(index: number): void {
    if (this.index === index) return;
    this.index = index;
    this.outSeq = null;
    this.out = false;
    this.done = false;
    this.failures = 0;
    this.wakeAt = null;
  }

  private send(index: number): AdvanceCommand[] {
    this.out = true;
    this.outSeq = ++this.seq;
    return [{ type: "next", fromIndex: index, seq: this.outSeq }];
  }
}
